import { useAtom, useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import {
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ForceEnableAllFlagsBanner,
  MissingFlagsmithEnvKeyBanner,
  MissingPostHogEnvKeyBanner,
  ReloadRequiredBanner,
} from '@/components/dev/feature-flags/feature-flags-banners';
import { FlagGroup } from '@/components/dev/feature-flags/feature-flags-group';
import {
  GROUP_ORDER,
  type GroupKey,
  PROVIDER_ORDER,
} from '@/components/dev/feature-flags/feature-flags-groups';
import {
  FeatureFlagsBulkActions,
  FeatureFlagsJumpTo,
} from '@/components/dev/feature-flags/feature-flags-toolbar';
import { type DevFlag, listAllFlags } from '@/features/flags-dev/discovery';
import { clearAllFavourites, favouritesAtom } from '@/features/flags-dev/favourites';
import {
  type FlagOverrideValue,
  clearAllOverrides,
  flagOverridesAtom,
  setManyOverrides,
} from '@/features/flags-dev/overrides';
import { Label } from '@heidi/design-system/label';
import { SearchInput } from '@heidi/design-system/search-input';
import { Switch } from '@heidi/design-system/switch';
import { TooltipProvider } from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';

const showChangedOnlyAtom = atomWithStorage<boolean>(
  'scribe.flags-dev.show-changed-only',
  false
);

/**
 * Lists every flag the SDKs (PostHog, Flagsmith via OpenFeature) know about and
 * forces each ON/OFF/default for this tab only. Discovery is cheap, so it
 * re-snapshots on every override change rather than caching.
 */
export const FeatureFlagsDevSection = () => {
  const currentOverrides = useAtomValue(flagOverridesAtom);
  const favouritesMap = useAtomValue(favouritesAtom);

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [showChangedOnly, setShowChangedOnly] = useAtom(showChangedOnlyAtom);

  const [refreshNonce, setRefreshNonce] = useState(0);

  // Memoized so a keystroke doesn't re-query both SDKs and invalidate every
  // downstream memo.
  const allFlags = useMemo(() => {
    // `listAllFlags()` reads both through module-level functions, so
    // exhaustive-deps cannot infer these two; referencing them keys the memo.
    void refreshNonce;
    void currentOverrides;
    return listAllFlags();
  }, [refreshNonce, currentOverrides]);

  const filteredFlags = useMemo(() => {
    const needle = deferredSearch.trim().toLowerCase();
    return allFlags.filter((flag) => {
      if (showChangedOnly && !flag.override) return false;
      if (!needle) return true;
      if (flag.key.toLowerCase().includes(needle)) return true;
      if (flag.description?.toLowerCase().includes(needle)) return true;
      return false;
    });
  }, [allFlags, deferredSearch, showChangedOnly]);

  // A favourited flag lands ONLY in 'favourites', so a row's favourite state is
  // derivable from group membership and no row re-reads the store.
  const groupedFlags = useMemo(() => {
    const groups: Record<GroupKey, DevFlag[]> = {
      favourites: [],
      posthog: [],
      flagsmith: [],
    };
    for (const flag of filteredFlags) {
      const isFav = favouritesMap[`${flag.provider}:${flag.key}`] === true;
      if (isFav) {
        groups.favourites.push(flag);
      } else {
        groups[flag.provider].push(flag);
      }
    }
    // Favourites sort by `${provider}:${key}` so mixed-provider order is stable.
    groups.favourites.sort((a, b) =>
      `${a.provider}:${a.key}`.localeCompare(`${b.provider}:${b.key}`)
    );
    for (const provider of PROVIDER_ORDER) {
      groups[provider].sort((a, b) => a.key.localeCompare(b.key));
    }
    return groups;
  }, [filteredFlags, favouritesMap]);

  const groupCounts = Object.fromEntries(
    GROUP_ORDER.map((groupKey) => [groupKey, groupedFlags[groupKey].length])
  ) as Record<GroupKey, number>;

  const overrideCount = allFlags.reduce(
    (count, flag) => (flag.override ? count + 1 : count),
    0
  );

  const favouriteCount = Object.keys(favouritesMap).length;

  // Otherwise "Reset all" leaves the user staring at an empty list.
  useEffect(() => {
    if (overrideCount === 0 && showChangedOnly) {
      setShowChangedOnly(false);
    }
  }, [overrideCount, setShowChangedOnly, showChangedOnly]);

  const setAllFiltered = (value: FlagOverrideValue) => {
    setManyOverrides(
      filteredFlags.map((flag) => ({
        provider: flag.provider,
        flagKey: flag.key,
        value,
      }))
    );
  };

  // Measured so the per-provider sub-headers stick at this header's bottom edge.
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;
    const update = () => setStickyHeaderHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Collapsed by default to keep mount cheap; filtering force-expands.
  const [expanded, setExpanded] = useState<Set<GroupKey>>(() => new Set());
  const isFiltering = deferredSearch.trim().length > 0 || showChangedOnly;
  const groupRefs = useRef<Record<GroupKey, HTMLDivElement | null>>({
    favourites: null,
    posthog: null,
    flagsmith: null,
  });
  const toggleGroup = (groupKey: GroupKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };
  const jumpToGroup = (groupKey: GroupKey) => {
    // Expand first, then defer a frame so the group is mounted and laid out.
    setExpanded((prev) => (prev.has(groupKey) ? prev : new Set(prev).add(groupKey)));
    requestAnimationFrame(() => {
      groupRefs.current[groupKey]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  return (
    // One provider for the whole panel: the dev sheet mounts outside the app's
    // TooltipProvider, and per-row CopyButtons each need a Tooltip ancestor.
    <TooltipProvider>
      <section aria-labelledby="dev-tools-feature-flags-heading">
        <div
          ref={stickyHeaderRef}
          className="sticky top-0 z-20 -mr-2 bg-surface-elevated pb-3 pr-2 pt-1"
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <TypographyP2SemiBold id="dev-tools-feature-flags-heading">
              Feature flags
            </TypographyP2SemiBold>
            <FeatureFlagsBulkActions
              filteredCount={filteredFlags.length}
              overrideCount={overrideCount}
              favouriteCount={favouriteCount}
              onSetAll={setAllFiltered}
              // In a desktop e2e build the baked-in pins return on the next
              // reload — see `clearAllOverrides`.
              onResetOverrides={() => clearAllOverrides()}
              onClearFavourites={() => clearAllFavourites()}
            />
          </div>
          <TypographyCaption className="mb-3 text-text-tertiary">
            Tab-scoped overrides. Survive reload, cleared when you close the tab.
          </TypographyCaption>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search flags by key or description"
            aria-label="Search feature flags"
            data-testid="dev-tools-feature-flags-search"
          />
          <Label className="mt-2 flex cursor-pointer select-none items-center gap-2">
            <Switch
              size="xs"
              checked={showChangedOnly}
              onCheckedChange={setShowChangedOnly}
              disabled={overrideCount === 0}
              data-testid="dev-tools-feature-flags-changed-only"
            />
            <TypographyCaption className="text-text-secondary">
              Show changed only
              {overrideCount > 0 ? ` (${overrideCount})` : ''}
            </TypographyCaption>
          </Label>
          <FeatureFlagsJumpTo
            counts={groupCounts}
            onJump={jumpToGroup}
            onRefresh={() => setRefreshNonce((n) => n + 1)}
          />
          {/* Inside the sticky header so it follows the user down the list. */}
          <ReloadRequiredBanner />
        </div>
        <MissingFlagsmithEnvKeyBanner />
        <MissingPostHogEnvKeyBanner />
        <ForceEnableAllFlagsBanner />
        {filteredFlags.length === 0 ? (
          <TypographyCaption className="text-text-tertiary">
            {showChangedOnly && deferredSearch.trim()
              ? `No changed flags match “${deferredSearch.trim()}”.`
              : showChangedOnly
                ? 'No changed flags.'
                : `No flags match “${deferredSearch.trim()}”.`}
          </TypographyCaption>
        ) : (
          <div className="flex flex-col gap-5">
            {GROUP_ORDER.map((groupKey) => (
              <FlagGroup
                key={groupKey}
                groupKey={groupKey}
                flags={groupedFlags[groupKey]}
                isFiltering={isFiltering}
                isExpanded={isFiltering || expanded.has(groupKey)}
                onToggle={() => toggleGroup(groupKey)}
                setRef={(el) => {
                  groupRefs.current[groupKey] = el;
                }}
                stickyTopPx={stickyHeaderHeight}
              />
            ))}
          </div>
        )}
      </section>
    </TooltipProvider>
  );
};
